import type { ReactNode } from 'react';
import {
  ArrowDown,
  ArrowUp,
  ChevronsUpDown,
  Download,
  FileSpreadsheet,
  FileText,
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Dropdown, DropdownItem } from '../../navigation/Dropdown';
import { resolveAlign } from './column-types';
import { ALIGN_CLASS, DENSITY_HEADER, type Density } from './styles';
import type { BandSegment } from './band-segments';
import type { DataGridColumn, DataGridSortState } from './types';

/** Props passed to the header when export lives inside the last <th>. */
export interface HeaderExportSlot {
  csv: boolean;
  excel: boolean;
  canExport: boolean;
  isExporting: boolean;
  onExportCsv: () => void;
  onExportExcel: () => void;
}

export interface DataGridHeaderProps<T> {
  columns: DataGridColumn<T>[];
  bandSegments: BandSegment[] | null;
  hasBands: boolean;
  density: Density;
  effectiveStickyHeader: boolean;
  effectiveSort: DataGridSortState | null;
  onSortClick: (col: DataGridColumn<T>) => void;
  /** When set, renders a compact export dropdown in an extra <th> at the end. */
  headerExport?: HeaderExportSlot | null;
}

/**
 * `<thead>` subtree — optional band row on top, then the main header
 * row with sort buttons. When `headerExport` is provided an extra cell
 * is appended at the far right with a small download icon dropdown,
 * saving the vertical space that the full toolbar would take on simple
 * listing tables.
 */
export function DataGridHeader<T>({
  columns,
  bandSegments,
  hasBands,
  density,
  effectiveStickyHeader,
  effectiveSort,
  onSortClick,
  headerExport,
}: DataGridHeaderProps<T>): ReactNode {
  // `will-change: transform` promeut chaque cellule sticky sur son propre
  // compositor layer GPU, ce qui evite les re-calculs de layout en cascade
  // quand la scrollbar horizontale ou le virtualizer body (US-C matrice
  // ~40 colonnes) changent de dimensions en fin de scroll — sans ça, Chrome
  // repaint le thead entier a chaque frame et provoque un blink visible.
  const stickyBand = effectiveStickyHeader
    ? 'sticky top-0 z-30 [will-change:transform]'
    : '';
  const stickyHeader = effectiveStickyHeader
    ? hasBands
      ? 'sticky top-[35px] z-20 [will-change:transform]'
      : 'sticky top-0 z-20 [will-change:transform]'
    : '';

  return (
    <thead className="bg-neutral-50 [&_tr]:border-b [&_tr]:border-neutral-200">
      {bandSegments && hasBands && (
        <tr className="h-9">
          {bandSegments.map((segment, idx) => (
            <th
              key={`band-${idx}`}
              scope="colgroup"
              colSpan={segment.span}
              className={cn(
                'h-9 font-semibold text-neutral-700 text-xs uppercase tracking-wider bg-neutral-100',
                'border-b border-neutral-200 border-r border-r-neutral-200 last:border-r-0',
                segment.band ? 'px-3' : '',
                ALIGN_CLASS[segment.band?.align ?? 'center'],
                segment.band?.className,
                stickyBand
              )}
            >
              {segment.band?.label ?? ''}
            </th>
          ))}
          {/* Extra band cell spanning the export column so alignment is preserved */}
          {headerExport && <th className={cn('h-9 bg-neutral-100', stickyBand)} />}
        </tr>
      )}
      <tr>
        {columns.map((col) => {
          const align = resolveAlign(col);
          const canSort =
            col.sortable !== false &&
            (col.accessor !== undefined || col.sortValue !== undefined);
          const isSorted = effectiveSort?.columnId === col.id;
          const direction = isSorted ? effectiveSort!.direction : null;

          return (
            <th
              key={col.id}
              scope="col"
              style={
                col.width || col.maxWidth
                  ? { width: col.width, maxWidth: col.maxWidth }
                  : undefined
              }
              className={cn(
                'align-middle font-medium text-neutral-600 text-xs uppercase tracking-wider bg-neutral-50',
                DENSITY_HEADER[density],
                ALIGN_CLASS[align],
                col.headerClassName,
                stickyHeader
              )}
            >
              {canSort ? (
                <button
                  type="button"
                  onClick={() => onSortClick(col)}
                  className={cn(
                    'inline-flex items-center gap-1 rounded hover:text-dark-500 transition-colors',
                    align === 'right' && 'flex-row-reverse',
                    isSorted && 'text-dark-500'
                  )}
                >
                  <span>{col.header}</span>
                  {direction === 'asc' ? (
                    <ArrowUp className="h-3.5 w-3.5" />
                  ) : direction === 'desc' ? (
                    <ArrowDown className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" />
                  )}
                </button>
              ) : (
                <span>{col.header}</span>
              )}
            </th>
          );
        })}

        {/* Compact export dropdown — renders as a small icon in the last <th> */}
        {headerExport && (
          <th
            className={cn(
              'align-middle bg-neutral-50 px-2',
              stickyHeader
            )}
            style={{ width: '44px' }}
          >
            <Dropdown
              align="right"
              trigger={
                <button
                  type="button"
                  disabled={!headerExport.canExport || headerExport.isExporting}
                  className="p-1.5 rounded hover:bg-neutral-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Exporter"
                >
                  <Download className="h-4 w-4 text-neutral-500" />
                </button>
              }
            >
              {headerExport.csv && (
                <DropdownItem
                  icon={<FileText className="h-4 w-4" />}
                  onClick={headerExport.onExportCsv}
                >
                  CSV
                </DropdownItem>
              )}
              {headerExport.excel && (
                <DropdownItem
                  icon={<FileSpreadsheet className="h-4 w-4" />}
                  onClick={headerExport.onExportExcel}
                >
                  Excel
                </DropdownItem>
              )}
            </Dropdown>
          </th>
        )}
      </tr>
    </thead>
  );
}
